/**
 * @module RainbowSDK
 */
import "./strophejs";
import adapter from "webrtc-adapter";
import { SDKPlugin } from "./plugins/plugin";
import { AuthService } from "./services/core/auth.service";
import { XmppService } from "./services/core/xmpp.service";
import { MainService } from "./services/core/main.service";
import { EventService } from "./services/core/event.service";
import { SettingsService } from "./services/core/settings.service";
import { ContactService } from "./services/core/contact.service";
import { ConnectionService, ConnectionServiceRB } from "./services/core/connection.service";
import { ExternalLoggerInterface, LogLevelEnum, LoggerService } from "./services/core/logger.service";
import { PresenceHelperService } from "./services/core/presenceHelper.service";
import jquery from "jquery";
import { UserNetworkService } from "./services/core/userNetwork.service";
import { BUBBLE_CONFERENCE_SVC, BubbleConferenceService, ConversationService, DirectorySearchService, ErrorHelperService, I18N_SVC, I18nService, NOTIF_VIEWER_SVC, NotificationsViewerService, SETTINGS_SVC } from "./services";
import { DirectorySearchServiceRB } from "./services/core/directorySearch.service";
import { ConnectedUser } from "./models";
import { UtilitiesService } from "./services/utilities/utilities.service";
import { BubbleService } from "./services/bubbles";
import { BUBBLE_SVC } from "./services/bubbles/bubble.service";
import { ConversationServiceRB } from "./services/conversation/conversation.service";
import { WEBINAR_SVC, WebinarService } from "./services/webinars/webinars.service";
import { CALL_SVC, CallService } from "./services/call/call.service";
import { FAVORITES_SVC, FavoritesService } from "./services/favorites/favorites.service";
import { CALL_LOG_SVC, CallLogService } from "./services/callLog/callLog.service";
import { DATA_CHANNEL_SVC, DataChannelService } from "./services/dataChannel";
import { DEVICE_MANAGEMENT_SVC, DeviceManagementService } from "./services/deviceManagement/deviceManagement.service";
import { TELEPHONYCONFIG_SVC, TelephonyConfigService } from "./services/telephonyConfig";
import { SDKAdminService, SDK_ADMIN_SVC } from "./services/admin";
import { BUBBLE_TAG_SVC, BubbleTagService } from "./services/bubbles/bubbleTag.service";
import { UserService, UserServiceRB } from "./services/users/user.service";
import { FILEDOCUMENT_SVC, FileDocumentService } from "./services/fileDocument/fileDocument.service";
import { LIVE_TRANSCRIPTION_SVC, LiveTranscriptionService } from "./services/transcription/liveTranscription.service";
import { TODO_SVC, TodoServiceRB } from "./services/todo/todo.service";
import { BubbleConferenceRecapService, CONFERENCE_RECAP_SVC } from "./services/conference/bubbleConferenceRecap.service";
import { CHANNEL_SVC, ChannelService } from "./services/channels/channel.service";
import { CALL_RECORDER_SVC, CallRecorderService } from "./services/call/callRecorder.service";
import { POLLS_SVC, PollsService } from "./services/polls/polls.service";
export interface RainbowSDKAppConfig {
    /** Rainbow backend server url */
    server?: string;

    /** Rainbow application identifier */
    applicationId?: string;

    /** Rainbow application secretKey */
    secretKey?: string;
}

export interface RainbowSDKMarkdownParser {
    markdownToHtml(markdown: string): string;
}

export interface RainbowSDKConfig {

    /** Application configuration */
    appConfig?: RainbowSDKAppConfig;

    /** List of plugins to use in the application */
    plugins?: SDKPlugin[];

    /** When set to true, the sdk will try to connect with localStorage token information */
    autoLogin?: boolean;

    /** Specify the SDK internal log level */
    logLevel?: LogLevelEnum;

    /** When set to true, sone additional CONNECTING events will be emitted to manage a connection progress bar */
    progressEvents?: boolean;

    /** Markdown parser implementation to use across SDK service */
    markdownParser?: RainbowSDKMarkdownParser;

    /** @internal */
    logger?: ExternalLoggerInterface;

    /** @internal */
    devMode?: boolean;
}

/**
 * This class is the **Web Rainbow SDK** main entry point
 * It offers :
 * + Lifecycle methods (create, start and stop methods)
 * + A get method to fetch plugin services
 * + Direct accesses to "core" plugins
 */
export class RainbowSDK {
    /**
     * Direct access to core ConnectedUser
     * @returns the ConnectedUser instance
     */
    public get connectedUser(): ConnectedUser | undefined { return this._contactService.userContact; }

    /**
     * Direct access to core ConnectionService
     * @returns the ConnectionService singleton instance
     */
    public get connectionService(): ConnectionService { return this._connectionService; }

    /**
      * Direct access to core ConversationService
      * @returns the ConversationService singleton instance if available
      */
    public get conversationService(): ConversationService { return this._conversationService; }

    /**
     * Direct access to core UserService
     * @returns the UserService singleton instance
     */
    public get userService(): UserService { return this._userService; }

    /**
     * Direct access to core UtilitiesService
     * @returns the UtilitiesService singleton instance
     */
    public get utils(): UtilitiesService { return this._utilitiesService; }

    /**
     * Direct access to core LoggerService
     * @returns the LoggerService singleton instance
     * @internal
     */
    public get loggerService(): LoggerService { return this._loggerService; }

    /**
     * Direct access to core i18nService
     * @returns the I18nService singleton instance
     * @internal
     */
    public get i18nService(): I18nService { return this.get<I18nService>(I18N_SVC); }

    /**
     * Direct access to core UserNetworkService
     * @returns the UserNetworkService singleton instance
     */
    public get userNetwork(): UserNetworkService { return ContactService.getInstance().userNetwork; }

    /**
     * Direct access to BubbleService
     * @returns the BubbleService singleton instance if available
     */
    public get bubbleService(): BubbleService | undefined { return this.get<BubbleService>(BUBBLE_SVC); }

    /**
     * Direct access to BubbleTagService
     * @returns the BubbleService singleton instance if available
     */
    public get BubbleTagService(): BubbleTagService | undefined { return this.get<BubbleTagService>(BUBBLE_TAG_SVC); }

    /**
     * Direct access to WebinarService
     * @returns the WebinarService singleton instance if available
     */
    public get webinarService(): WebinarService | undefined { return this.get<WebinarService>(WEBINAR_SVC); }

    /**
     * Direct access to FileDocumentService
     * @returns the DocumentsService singleton instance if available
     */
    public get fileDocumentService(): FileDocumentService | undefined { return this.get<FileDocumentService>(FILEDOCUMENT_SVC); }

    /**
     * Direct access to core DirectoryService
     * @returns the DirectoryService singleton instance
     */
    public get directorySearchService(): DirectorySearchService { return this._directorySearchService; }

    /**
     * Direct access to CallService
     * @returns the CallService singleton instance if available
     */
    public get callService(): CallService | undefined { return this.get<CallService>(CALL_SVC); }

    /**
     * Direct access to CallRecorderService
     * @returns the CallService singleton instance if available
     */
    public get callRecorderService(): CallRecorderService | undefined { return this.get<CallRecorderService>(CALL_RECORDER_SVC); }

    /**
     * Direct access to FavoritesService
     * @returns the FavoritesService singleton instance if available
     */
    public get favoritesService(): FavoritesService | undefined { return this.get<FavoritesService>(FAVORITES_SVC); }

    /**
     * Direct access to CallLogService
     * @returns the CallLogService singleton instance if available
     */
    public get callLogsService(): CallLogService | undefined { return this.get<CallLogService>(CALL_LOG_SVC); }

    /**
     * Direct access to BubbleConferenceService
     * @returns the BubbleConferenceService singleton instance if available
     */
    public get bubbleConferenceService(): BubbleConferenceService | undefined { return this.get<BubbleConferenceService>(BUBBLE_CONFERENCE_SVC); }

    /**
     * Direct access to DataChannelService
     * @returns the DataChannelService singleton instance if available
     */
    public get dataChannelService(): DataChannelService | undefined { return this.get<DataChannelService>(DATA_CHANNEL_SVC); }

    /**
     * Direct access to DeviceManagementService
     * @returns the DeviceManagementService singleton instance if available
     */
    public get deviceManagementService(): DeviceManagementService | undefined { return this.get<DeviceManagementService>(DEVICE_MANAGEMENT_SVC); }

    /**
     * Direct access to TelephonyConfigService
     * @returns the TelephonyConfigService singleton instance if available
     */
    public get telephonyConfigService(): TelephonyConfigService | undefined { return this.get<TelephonyConfigService>(TELEPHONYCONFIG_SVC); }

    /**
     * Direct access to TranscriptionService
     * @returns the TranscriptionService singleton instance if available
     */
    public get transcriptionService(): LiveTranscriptionService | undefined { return this.get<LiveTranscriptionService>(LIVE_TRANSCRIPTION_SVC); }

    /**
     * Direct access to BubbleConferenceRecapService
     * @returns the BubbleConferenceRecapService singleton instance if available
     */
    public get conferenceRecapService(): BubbleConferenceRecapService | undefined { return this.get<BubbleConferenceRecapService>(CONFERENCE_RECAP_SVC); }

    /**
     * Direct access to ChannelService
     * @returns the ChannelService singleton instance if available
     */
    public get channelService(): ChannelService | undefined { return this.get<ChannelService>(CHANNEL_SVC); }

    /**
     * Direct access to ChannelService
     * @returns the ChannelService singleton instance if available
     */
    public get settingsService(): SettingsService { return this.get<SettingsService>(SETTINGS_SVC); }

    /**
     * Direct access to TodoServiceRB
     * @returns the TodoServiceRB singleton instance if available
     */
    public get todoService(): TodoServiceRB | undefined { return this.get<TodoServiceRB>(TODO_SVC); }

    /**
     * Direct access to PollsService
     * @returns the PollsService singleton instance if available
     */
    public get pollsService(): PollsService | undefined { return this.get<PollsService>(POLLS_SVC); }

    /**
     * Direct access to NotificationsViewerService
     * @returns the NotificationsViewerService singleton instance if available
     */
    public get notificationService(): NotificationsViewerService { return this.get<NotificationsViewerService>(NOTIF_VIEWER_SVC); }

    /**
     * Direct access to AdminService
     * @returns the AdminService singleton instance if available
     */
    public get adminService(): SDKAdminService | undefined { return this.get<SDKAdminService>(SDK_ADMIN_SVC); }

    public markdownParser: RainbowSDKMarkdownParser | undefined;

    private _contactService: ContactService = ContactService.getInstance();
    private _userService: UserService = UserServiceRB.getInstance();
    private _loggerService: LoggerService = LoggerService.getInstance();
    private _connectionService: ConnectionServiceRB = ConnectionServiceRB.getInstance();
    private _utilitiesService: UtilitiesService = UtilitiesService.getInstance();
    private _conversationService = ConversationServiceRB.getInstance();
    private _errorHelperService = ErrorHelperService.getInstance();

    private _directorySearchService: DirectorySearchService;

    /**
     * Static method to reach the RainbowSDK singleton instance
     * @returns The Rainbow singleton instance
     */
    public static getInstance(): RainbowSDK {
        if (!window.rainbow) { window.rainbow = { plugins: {}, rainbowSDK: undefined, services: {} }; }
        if (!window.rainbow.rainbowSDK) window.rainbow.rainbowSDK = new RainbowSDK();
        return window.rainbow.rainbowSDK as RainbowSDK;
    }

    /**
     * Static method to create the RainbowSDK singleton instance
     * @returns The Rainbow singleton instance
     */
    public static create(options?: RainbowSDKConfig): RainbowSDK {
        const rainbowSDK = RainbowSDK.getInstance();
        window.adapter = adapter;

        if (!window.moment) (window as any).moment = null;
        if (!window.moduleIds) window.moduleIds = [];

        (globalThis as any).$ = jquery;
        const config = (window as any).config;
        const version = (window as any).version ?? 'sdk';

        if (version === 'sdk') {
            (window as any).version = rainbowSDK.getVersion();
            config.multiEnvServer = null;
            config.consoleLog = true;
            config.sdk = true;
        }

        //config.telemetry = true;
        //config.webinarMultiSubscribe = true;

        if (!config.xmpp) config.xmpp = { protocol: "wss", server: "demo.openrainbow.org", currentServer: "demo.openrainbow.org", port: "443", pingInterval: "60000" };
        if (!config.webservices) config.webservices = { protocol: "https", server: "demo.openrainbow.org", currentServer: "demo.openrainbow.org", port: "443" };

        if (options?.appConfig) {
            if (options.appConfig.server) config.xmpp.server = config.webservices.server = options.appConfig.server;
            if (options.appConfig.applicationId) config.rainbowApiClientKey = options.appConfig.applicationId;
            if (options.appConfig.secretKey) config.appModuleKey = options.appConfig.secretKey;
        }

        if (options?.logger) rainbowSDK._loggerService.configure(options?.logger);
        if (options?.markdownParser) rainbowSDK.markdownParser = options.markdownParser;

        const authService = AuthService.getInstance();
        authService.getServerApiUrls();
        authService.browserFingerprint = "NoImprint";

        XmppService.getInstance();
        SettingsService.getInstance().configure();
        SettingsService.getInstance().setSetting("apiMode", "rainbow");

        if (options.devMode === true) {
            SettingsService.getInstance().setSetting("devMode", "true");
            SettingsService.getInstance().setSetting("showBodyMessage", "true");
        }

        MainService.getInstance().configure();
        EventService.getInstance().configure();
        PresenceHelperService.getInstance();

        if (options?.plugins) options.plugins.forEach((plugin: SDKPlugin) => { plugin.initialize(); });
        rainbowSDK._directorySearchService = DirectorySearchServiceRB.getInstance();

        if (options?.autoLogin) rainbowSDK._connectionService.autoLogin = options.autoLogin;
        if (options?.progressEvents) rainbowSDK._connectionService.progressEvents = options.progressEvents;
        if (options?.logLevel !== undefined) rainbowSDK._loggerService.logLevel = options.logLevel;

        // Create the globalAudioTag if necessary
        if (!document.getElementById("globalAudioTag")) {
            const audio = document.createElement("audio");
            audio.id = "globalAudioTag";
            audio.autoplay = true;
            audio.style.display = "none";
            document.body.appendChild(audio);
        }

        return rainbowSDK;
    }

    private constructor() {}

    /**
     * Permits to enable console logs
     * @param enable - when set to true logs are displayed on console
     */
    public enableLogs(enable: boolean): void {
        this._loggerService.enableConsoleLog = enable;
    }

    /**
     * Permits to specify logLevel
     * @param logLevel - when set to true logs are displayed on console
     */
    public setLogLevel(logLevel: LogLevelEnum): void {
        this._loggerService.logLevel = logLevel;
    }

    /**
     * Permits to get current logLevel
     */
    public getLogLevel(): LogLevelEnum {
        return this._loggerService.logLevel;
    }

    /**
     * Permits to start the Rainbow SDK
     * @returns resolve when Rainbow SDK is started
     */
    public async start(): Promise<ConnectedUser | undefined> {
        return await this.connectionService.start();
    }

    /**
    * Permits to stop the Rainbow SDK
    * @returns resolve when Rainbow SDK is stopped
    */
    public async stop(): Promise<void> {
        await this.connectionService.stop();
    }

    /**
    * Get the current Rainbow SDK version
    * @returns the current Rainbow SDK version
    */
    public getVersion(): string {
        return "5.0.43-sts";
    }

    /**
    * Permit to fetch the singleton instance of a named service
    * @returns the singleton instance of a named service
    */
    public get<T = any>(serviceName: string): T | undefined {
        return window.rainbow.services[serviceName];
    }
}
